import { HStack, VStack } from "@chakra-ui/react";
import { LocalDate } from "@js-joda/core";
import { useQuery } from "@tanstack/react-query";
import { auth } from "../../../../../core/auth";
import useApi from "../../../../../shared/hooks/useApi";
import type { PatientId } from "../../../../../shared/schema/schema";
import { dateFormatter } from "../../../../../shared/utils/date-formatter";
import EntityProfileDataTable, {
  type EntityProfileDataTableColumn,
} from "../../../../entityProfileModal/components/EntityProfileDataTable";
import {
  EntityProfileEmptyState,
  EntityProfileErrorState,
  EntityProfileLoadingState,
} from "../../../../entityProfileModal/components/EntityProfileFeedback";
import type { PatientProfileBridgeActions } from "../../../patientProfileModal.actions";
import type { PatientProfilePatient } from "../../../patientProfileModal.types";
import EntityModalButton from "../../../../entityProfileModal/components/EntityModalButton";

type Props = {
  actions: PatientProfileBridgeActions;
  contracts: PatientProfilePatient["contracts"];
  patient?: PatientProfilePatient;
  patientId: PatientId;
  onDone: () => void;
};

type PatientContractDiagnosisCode = {
  endDate?: string | null;
  formattedDxCode?: string | null;
  id: number;
  isForBilling?: boolean | null;
  patientContractId?: number | null;
  shortDesc?: string | null;
  startDate?: string | null;
  type?: "PRINCIPAL" | "SURGICAL" | "OTHER" | null;
};

type PatientContractDiagnosisCodesResponse = {
  patientContractDiagnosisCodes: PatientContractDiagnosisCode[];
};

type DiagnosisCodeSortKey =
  | "id"
  | "contract"
  | "type"
  | "dxCode"
  | "shortDescription"
  | "startDate"
  | "endDate"
  | "isForBilling";

function formatDate(value: string | null | undefined) {
  return value === null || value === undefined || value === ""
    ? ""
    : dateFormatter.toDate(LocalDate.parse(value.slice(0, 10)));
}

function formatDiagnosisType(type: PatientContractDiagnosisCode["type"]) {
  switch (type) {
    case "PRINCIPAL":
      return "Principal diagnosis";
    case "SURGICAL":
      return "Surgical procedure";
    case "OTHER":
    case null:
    case undefined:
      return "Other pertinent diagnosis";
  }
}

function formatYesNo(value: boolean | null | undefined) {
  if (value === null || value === undefined) {
    return "";
  }

  return value ? "Yes" : "No";
}

function getContractName(
  contracts: PatientProfilePatient["contracts"],
  contractId: number | null | undefined
) {
  if (contractId === null || contractId === undefined) {
    return "Default";
  }

  const contract = contracts.find((patientContract) => patientContract.id === contractId);

  return contract?.contractTypeName ?? `Contract ${contractId}`;
}

export default function PatientProfileDiagnosisCodesSection(props: Props) {
  const { api } = useApi();
  const canEdit = auth.isAllowedTo("edit_patient_page_diagnosis_codes");
  const diagnosisCodesQuery = useQuery({
    queryKey: ["patientProfileModal", props.patientId, "administrativeDiagnosisCodes"],
    queryFn: () =>
      api.anyRequest<PatientContractDiagnosisCodesResponse>(
        "GET",
        "/agencies/:agencyId/agency_members/:agencyMemberId/patients/:patientId/patient_contract_diagnosis_codes",
        { path: { patientId: props.patientId } }
      ),
  });
  const diagnosisCodes = diagnosisCodesQuery.data?.patientContractDiagnosisCodes ?? [];
  const openDiagnosisCodeModal = (data?: PatientContractDiagnosisCode) => {
    if (props.patient === undefined) {
      return;
    }

    props.actions.openPatientAdministrativeDiagnosisCode({
      patient: props.patient,
      data,
      onDone: () => {
        props.onDone();
        void diagnosisCodesQuery.refetch();
      },
    });
  };
  const columns: Array<
    EntityProfileDataTableColumn<PatientContractDiagnosisCode, DiagnosisCodeSortKey>
  > = [
    {
      key: "id",
      header: "ID",
      render: (row) => row.id,
      sortKey: "id",
      sortValue: (row) => row.id,
    },
    {
      key: "contract",
      header: "Contract",
      render: (row) => getContractName(props.contracts, row.patientContractId),
      sortKey: "contract",
      sortValue: (row) => getContractName(props.contracts, row.patientContractId),
    },
    {
      key: "type",
      header: "Type",
      render: (row) => formatDiagnosisType(row.type),
      sortKey: "type",
      sortValue: (row) => formatDiagnosisType(row.type),
    },
    {
      key: "dxCode",
      header: "DX Code",
      render: (row) => row.formattedDxCode ?? "",
      sortKey: "dxCode",
      sortValue: (row) => row.formattedDxCode ?? "",
    },
    {
      key: "shortDescription",
      header: "Short description",
      render: (row) => row.shortDesc ?? "",
      sortKey: "shortDescription",
      sortValue: (row) => row.shortDesc ?? "",
    },
    {
      key: "startDate",
      header: "Start date",
      render: (row) => formatDate(row.startDate),
      sortKey: "startDate",
      sortValue: (row) => row.startDate ?? "",
    },
    {
      key: "endDate",
      header: "End date",
      render: (row) => formatDate(row.endDate),
      sortKey: "endDate",
      sortValue: (row) => row.endDate ?? "",
    },
    {
      key: "isForBilling",
      header: "Is for billing",
      render: (row) => formatYesNo(row.isForBilling),
      sortKey: "isForBilling",
      sortValue: (row) => formatYesNo(row.isForBilling),
    },
  ];

  return (
    <VStack alignItems="stretch" spacing={4}>
      {canEdit ? (
        <HStack justifyContent="flex-end">
          <EntityModalButton
            disabled={props.patient === undefined}
            iconClassName="fa fa-plus"
            kind="blue"
            size="sm"
            onClick={() => openDiagnosisCodeModal()}
          >
            Add Diagnosis
          </EntityModalButton>
        </HStack>
      ) : null}

      {diagnosisCodesQuery.isLoading ? (
        <EntityProfileLoadingState label="Loading diagnosis codes..." />
      ) : diagnosisCodesQuery.isError ? (
        <EntityProfileErrorState
          label="Could not get patient diagnosis list"
          onRetry={() => void diagnosisCodesQuery.refetch()}
        />
      ) : diagnosisCodes.length === 0 ? (
        <EntityProfileEmptyState>No diagnosis codes.</EntityProfileEmptyState>
      ) : (
        <EntityProfileDataTable
          columns={columns}
          defaultSort={{ key: "id", direction: "desc" }}
          getRowKey={(row) => row.id}
          rows={diagnosisCodes}
        />
      )}
    </VStack>
  );
}
